import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Logo from "../../Images/pari_logo_6.png";
import rightSidePic from "../../Images/right_side_pic.png";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { LogOut } from "../../Redux/Actions/authAction";
import { respondToRequests } from "../../Redux/Actions/userStatusService";

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    subtitle1: {
      color: '#666',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '10px 24px',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

export default function Requests() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.user);
  const userInfo = authState?.userInfo || {};
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  // Process userInfo to extract pending requests
  useEffect(() => {
    
    // Check if userInfo has userRoles directly (new structure)
    const userRoles = userInfo?.userRoles || [];
    
    if (userRoles.length > 0) {
      const pendingRequests = userRoles
        .filter(role => role.role === 'SUB_MANAGER')
        .flatMap(role => 
          role.parents?.filter(parent => parent.status === 'PENDING').map(parent => ({
            id: parent.id,
            entityName: parent.parentUserRole?.entityInfo?.name || 'Unknown Entity',
            parentRole: parent.parentUserRole?.role || 'Unknown Role',
            status: parent.status,
            parentUserRole: parent.parentUserRole,
            createdAt: parent.createdAt
          })) || []
        );
      
      setRequests(pendingRequests);
    }
  }, [userInfo, authState]);

  const handleAccept = async (requestId) => {
    setLoading(true);
    try {
      // TODO: Add API call to accept the request
      // const response = await dispatch(AcceptRequest({ requestId }));
      
      setRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, status: 'ACCEPTED' }
            : req
        )
      );
    } catch (error) {
      console.error("Error accepting request:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async (requestId) => {
    setLoading(true);
    try {
      // TODO: Add API call to decline the request
      // const response = await dispatch(DeclineRequest({ requestId }));
      
      setRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, status: 'DENIED' }
            : req
        )
      );
    } catch (error) {
      console.error("Error declining request:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    setLoading(true);
    try {
      const markedRequests = requests.filter(req => req.status === 'ACCEPTED' || req.status === 'DENIED');
      
      if (markedRequests.length === 0) {
        console.error("No requests marked for submission");
        return;
      }

      // Prepare request body for API
      const requestBody = markedRequests.map(req => ({
        relationId: req.id,
        status: req.status
      }));


      // Call API to respond to requests
      const response = await dispatch(respondToRequests(requestBody));
      
      if (response?.payload?.statusCode === 200) {
             // Route based on number of selections
        
          dispatch(LogOut());
          navigate("/login");
      
      } else {
        console.error("Failed to respond to requests:", response);
      }
    } catch (error) {
      console.error("Error submitting requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    dispatch(LogOut());
    navigate("/login");
  };

  const pendingRequests = requests.filter(req => req.status === 'PENDING');
  const markedRequests = requests.filter(req => req.status === 'ACCEPTED' || req.status === 'DENIED');

  return (
    <ThemeProvider theme={theme}>
      <Box className="login-container" sx={{ 
        display: 'flex', 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
      }}>
        {/* Left side - Requests form */}
        <Box className="login-form-container" sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem'
        }}>
          <Container 
            maxWidth={false}
            sx={{
              maxWidth: '500px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <div sx={{ 
              width: '100%',
              padding: '2.5rem',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
             
              backdropFilter: 'blur(10px)'
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box component="span">
                      <img 
                        src={Logo} 
                        alt="Company Logo" 
                        style={{ 
                          height: '32px', 
                          width: 'auto',
                          objectFit: 'contain'
                        }} 
                      />
                    </Box>
                    <Chip 
                      label="Manager Panel"
                      sx={{ 
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        border: '1px solid #1976d2',
                        fontWeight: 500
                      }}
                    />
                  </Typography>
                </Box>

                {/* Main Content */}
                <Box>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      mb: 1, 
                      fontSize: '1.5rem', 
                      fontWeight: 600,
                      color: '#333'
                    }}
                  >
                    See Requests
                  </Typography>
                  
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      mb: 3, 
                      color: '#666', 
                      fontSize: '0.9rem',
                      lineHeight: 1.4
                    }}
                  >
                    Following managers would like to add you as a sub-manager
                  </Typography>
                </Box>

                {/* Requests List */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {requests.map((request) => (
                    <Box 
                      key={request.id}
                      sx={{ 
                        p: 2.5, 
                        border: request.status === 'ACCEPTED' ? '2px solid #4caf50' : 
                                request.status === 'DENIED' ? '2px solid #f44336' : 
                                '1px solid #e0e0e0',
                        backgroundColor: request.status === 'ACCEPTED' ? '#f1f8e9' : 
                                       request.status === 'DENIED' ? '#ffebee' : 
                                       '#fafafa',
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: request.status === 'ACCEPTED' ? '#e8f5e8' : 
                                         request.status === 'DENIED' ? '#ffebee' : 
                                         '#f5f5f5',
                          transition: 'background-color 0.2s ease'
                        }
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          mb: 1, 
                          fontWeight: 500,
                          color: '#333',
                          fontSize: '0.9rem'
                        }}
                      >
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {request.entityName}
                        </Box> ({request.parentRole}) has invited you as a sub-manager
                      </Typography>
                      
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#666',
                          fontSize: '0.75rem',
                          display: 'block',
                          mb: 2
                        }}
                      >
                        Requested on: {new Date(request.createdAt).toLocaleDateString()}
                      </Typography>

                      {/* Status indicator */}
                      {request.status !== 'PENDING' && (
                        <Box sx={{ mb: 2 }}>
                          <Chip
                            label={request.status === 'ACCEPTED' ? '✓ Accepted' : '✗ Denied'}
                            sx={{
                              bgcolor: request.status === 'ACCEPTED' ? '#4caf50' : '#f44336',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                          />
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                          variant={request.status === 'ACCEPTED' ? "contained" : "outlined"}
                          startIcon={<CheckCircleIcon sx={{ fontSize: '16px' }} />}
                          onClick={() => handleAccept(request.id)}
                          disabled={loading}
                          sx={{
                            bgcolor: request.status === 'ACCEPTED' ? '#4caf50' : 'transparent',
                            borderColor: request.status === 'ACCEPTED' ? '#4caf50' : '#1976d2',
                            color: request.status === 'ACCEPTED' ? 'white' : '#1976d2',
                            '&:hover': { 
                              bgcolor: request.status === 'ACCEPTED' ? '#45a049' : 'rgba(25, 118, 210, 0.04)',
                              borderColor: request.status === 'ACCEPTED' ? '#45a049' : '#1565c0'
                            },
                            minWidth: '80px',
                            height: '32px'
                          }}
                        >
                          Accept
                        </Button>
                        <Button
                          variant={request.status === 'DENIED' ? "contained" : "outlined"}
                          startIcon={<CancelIcon sx={{ fontSize: '16px' }} />}
                          onClick={() => handleDecline(request.id)}
                          disabled={loading}
                          sx={{
                            bgcolor: request.status === 'DENIED' ? '#f44336' : 'transparent',
                            borderColor: request.status === 'DENIED' ? '#f44336' : '#333',
                            color: request.status === 'DENIED' ? 'white' : '#333',
                            '&:hover': {
                              bgcolor: request.status === 'DENIED' ? '#d32f2f' : 'rgba(0,0,0,0.04)',
                              borderColor: request.status === 'DENIED' ? '#d32f2f' : '#000'
                            },
                            minWidth: '80px',
                            height: '32px'
                          }}
                        >
                          Decline
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Continue Button */}
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleContinue}
                    disabled={markedRequests.length === 0 || loading}
                    sx={{
                      bgcolor: markedRequests.length === 0 ? '#ccc' : '#1557ec',
                      color: 'white',
                      py: 1.2,
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: markedRequests.length === 0 ? '#ccc' : '#1559ec'
                      },
                      '&:disabled': {
                        bgcolor: '#ccc',
                        color: '#999'
                      }
                    }}
                  >
                    {loading ? 'Processing...' : 'Continue'}
                  </Button>
                </Box>

                {/* Back Button */}
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      borderColor: '#666',
                      color: '#666',
                      py: 1.2,
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#333',
                        color: '#333',
                        backgroundColor: 'rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </Box>
            </div>
          </Container>
        </Box>

        {/* Right side - Background Image */}
        <Box className="login-background" sx={{ 
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          display: { xs: 'none', md: 'block' }
        }}>
          <img 
            src={rightSidePic} 
            alt="Requests Background" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'brightness(0.8) contrast(1.2)'
            }}
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
} 